import { Dispatch } from 'react';
import { AxiosResponse } from 'axios';
import Redux, { Dispatch as DispatchRedux } from 'redux';
import { OTP_PASSCODE_LENGTH } from '@app/constants';
import { handleShellConfig, setSignInData, signInErrorClean } from '@app/store/actions';
import { authService, basicService, myProfileService } from '@app/services';
import { returnInputNumber, validateInput } from '@app/helpers';
import { loginForms } from '@app/enums';
import { SetLoginForm } from '@app/models';

export const responseOnSuccess = (dispatch: Redux.Dispatch, rememberMe?: boolean) => (res: AxiosResponse) => {
    setSignInData(res, dispatch, rememberMe);
    dispatch<any>(handleShellConfig());
    dispatch(signInErrorClean());
    basicService.redirectAfterSignin();
};

export const goBackToList = (setLoginForm?: SetLoginForm) => () => {
    setLoginForm && setLoginForm(loginForms.twoFactorList);
};
export const handleGoToSignIn = (dispatch: DispatchRedux, setLoginForm: SetLoginForm) => () => {
    dispatch(signInErrorClean());
    setLoginForm(loginForms.unknown);
};
export const handleChangeInputOtp =
    (setOtpCode: React.Dispatch<string>, setIsError: React.Dispatch<boolean>, isError: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isError) {
            setIsError(false);
        }

        if (validateInput(OTP_PASSCODE_LENGTH, e.target.value)) {
            const numberValue: string = returnInputNumber(e.target.value);

            setOtpCode(numberValue);
        }
    };
export const timeoutWaitU2fRegister = 60;
export const u2fRegister = (onRegistrationCallback: () => void) => (setIsNeedStartTimer: Dispatch<boolean>, errorCallback: () => Promise<{}>) => {
    return myProfileService
        .u2fRegister(({ data }: AxiosResponse<string>) => {
            setIsNeedStartTimer(true);

            // TODO: Add method to modify response to correct data for navigator.credentials.create()
            let crt = JSON.parse(data);

            let publicKey = {
                challenge: coerceToArrayBuffer(crt.challenge, 'challenge'),
                rp: {
                    name: crt.rp.name,
                    displayName: crt.rp.displayName
                },

                user: {
                    id: coerceToArrayBuffer(crt.user.id, 'user.id'),
                    name: crt.user.name,
                    displayName: crt.user.displayName
                },

                pubKeyCredParams: crt.pubKeyCredParams,
                timeout: crt.timeout,
                attestation: crt.attestation,
                status: crt.status,
                errorMessage: crt.errorMessage
            };

            // Note: The following call will cause the authenticator to display UI.
            return navigator.credentials
                .create({ publicKey })
                .then((newCredential: any) => {
                    setIsNeedStartTimer(false);
                    let attestationObject = new Uint8Array(newCredential.response.attestationObject);
                    let clientDataJSON = new Uint8Array(newCredential.response.clientDataJSON);
                    let rawId = new Uint8Array(newCredential.rawId);
                    const data = {
                        id: newCredential.id,
                        rawId: coerceToBase64Url(rawId),
                        type: newCredential.type,
                        extensions: newCredential.getClientExtensionResults(),
                        response: {
                            AttestationObject: coerceToBase64Url(attestationObject),
                            clientDataJson: coerceToBase64Url(clientDataJSON)
                        }
                    };

                    myProfileService.u2fPostRegister(data);
                })
                .then(onRegistrationCallback)
                .catch(errorCallback);
        })
        .catch(errorCallback);
};

export const u2fTest = (onRegistrationCallback: () => void) => (setIsNeedStartTimer: Dispatch<boolean>, errorCallback: () => Promise<{}>) => {
    return myProfileService
        .u2fTestGet()
        .then(async ({ data }: AxiosResponse<string>) => {
            let assertionOptions = JSON.parse(data);
            const challenge = assertionOptions.challenge.replace(/-/g, '+').replace(/_/g, '/');

            setIsNeedStartTimer(true);
            assertionOptions.challenge = Uint8Array.from(atob(challenge), (c) => c.charCodeAt(0));

            assertionOptions.allowCredentials.forEach(function (listItem: any) {
                const fixedId = listItem.id.replace(/_/g, '/').replace(/-/g, '+');
                listItem.id = Uint8Array.from(atob(fixedId), (c) => c.charCodeAt(0));
            });

            // ask browser for credentials (browser will ask connected authenticators)
            let credential = await navigator.credentials.get({ publicKey: assertionOptions });
            let authenticatorAssertionRawResponse = createData(credential);

            return myProfileService.u2fTestPost(authenticatorAssertionRawResponse).then(({ data }: AxiosResponse) => {
                try {

                    if (data.status === 'ok') {
                        setIsNeedStartTimer(false);
                        onRegistrationCallback();
                    }
                } catch (e) {}
            });
        })
        .catch(errorCallback);
};

const createData = (credential: any) => {
    let authData = new Uint8Array(credential.response.authenticatorData);
    let clientDataJSON = new Uint8Array(credential.response.clientDataJSON);
    let rawId = new Uint8Array(credential.rawId);
    let sig = new Uint8Array(credential.response.signature);
    return {
        id: credential.id,
        rawId: coerceToBase64Url(rawId),
        type: credential.type,
        extensions: credential.getClientExtensionResults(),
        response: {
            authenticatorData: coerceToBase64Url(authData),
            clientDataJson: coerceToBase64Url(clientDataJSON),
            signature: coerceToBase64Url(sig)
        }
    };
};

export const u2fSignIn =
    (dispatch: Redux.Dispatch, contextToken: string) => (setIsNeedStartTimer: Dispatch<boolean>, errorCallback: () => Promise<{}>) => {
        authService.signInU2f(contextToken, '').catch(async ({ response }) => {
            const { error_uri, error_description } = response.data;

            if (error_description === 'u2f_verify') {
                setIsNeedStartTimer(true);
                let assertionOptions = JSON.parse(error_uri);
                const challenge = assertionOptions.challenge.replace(/-/g, '+').replace(/_/g, '/');

                assertionOptions.challenge = Uint8Array.from(atob(challenge), (c) => c.charCodeAt(0));

                assertionOptions.allowCredentials.forEach(function (listItem: any) {
                    const fixedId = listItem.id.replace(/_/g, '/').replace(/-/g, '+');
                    listItem.id = Uint8Array.from(atob(fixedId), (c) => c.charCodeAt(0));
                });

                return await navigator.credentials
                    .get({ publicKey: assertionOptions })
                    .then((response) => {
                        let authenticatorAssertionRawResponse = createData(response);

                        return authService.signInU2f(contextToken, JSON.stringify(authenticatorAssertionRawResponse));
                    })
                    .then(responseOnSuccess(dispatch))
                    .catch(errorCallback)
                    .finally(() => {
                        setIsNeedStartTimer(false);
                    });
            }
        });
    };

const coerceToBase64Url = function (thing: string | Iterable<number> | ArrayLike<number> | ArrayBuffer) {
    // Array or ArrayBuffer to Uint8Array
    if (Array.isArray(thing)) {
        thing = Uint8Array.from(thing);
    }
    if (thing instanceof ArrayBuffer) {
        thing = new Uint8Array(thing);
    }
    // Uint8Array to base64
    if (thing instanceof Uint8Array) {
        let str = '';
        const len = thing.byteLength;
        for (let i = 0; i < len; i++) {
            str += String.fromCharCode(thing[i]);
        }
        thing = window.btoa(str);
    }
    if (typeof thing !== 'string') {
        throw new Error('could not coerce to string');
    }
    // base64 to base64url
    // NOTE: "=" at the end of challenge is optional, strip it off here
    thing = thing.replace(/\+/g, '-').replace(/\//g, '_').replace(/=*$/g, '');
    return thing;
};
const coerceToArrayBuffer = (thing: string | Iterable<number> | ArrayBuffer, name: string) => {
    if (typeof thing === 'string') {
        // base64url to base64
        thing = thing.replace(/-/g, '+').replace(/_/g, '/');
        // base64 to Uint8Array
        const str = window.atob(thing);
        const bytes = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) {
            bytes[i] = str.charCodeAt(i);
        }
        thing = bytes;
    }
    // Array to Uint8Array
    if (Array.isArray(thing)) {
        thing = new Uint8Array(thing);
    }
    // Uint8Array to ArrayBuffer
    if (thing instanceof Uint8Array) {
        thing = thing.buffer;
    }
    // error if none of the above worked
    if (!(thing instanceof ArrayBuffer)) {
        throw new TypeError(`could not coerce '${name}' to ArrayBuffer`);
    }
    return thing;
};
